import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import PDFViewer from '../components/PDFViewer';
import { usePermission } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { ContentsAPI, UploadsAPI, UploadGroupsAPI, S3API } from '../api/api';
import {
    ChevronRight,
    ChevronLeft,
    X,
    Download,
    Maximize2,
    Minimize2,
    FolderOpen,
    Image,
    FileText,
    ExternalLink,
    Calendar,
    Target,
    TrendingUp,
    Award,
    BookOpen,
    Briefcase,
    Users,
    BarChart3,
    Lightbulb,
    Video
} from 'lucide-react';
import { LUCIDE_ICON_MAP } from '../components/LucideIconPicker';
import './VisionPage.css';

// 제목 기반 아이콘 매핑 함수
const getIconByTitle = (title, size = 48) => {
    const lowerTitle = title?.toLowerCase() || '';

    // 키워드 기반 아이콘 매핑
    if (lowerTitle.includes('대시보드') || lowerTitle.includes('dashboard')) {
        return <BarChart3 size={size} />;
    }
    if (lowerTitle.includes('시스템') || lowerTitle.includes('arena') || lowerTitle.includes('개발')) {
        return <Briefcase size={size} />;
    }
    if (lowerTitle.includes('본부') || lowerTitle.includes('직할') || lowerTitle.includes('그룹')) {
        return <Users size={size} />;
    }
    if (lowerTitle.includes('효율') || lowerTitle.includes('임차')) {
        return <TrendingUp size={size} />;
    }
    if (lowerTitle.includes('교육') || lowerTitle.includes('학습')) {
        return <BookOpen size={size} />;
    }
    if (lowerTitle.includes('목표') || lowerTitle.includes('비전')) {
        return <Target size={size} />;
    }
    if (lowerTitle.includes('성과') || lowerTitle.includes('평가')) {
        return <Award size={size} />;
    }
    if (lowerTitle.includes('일정') || lowerTitle.includes('캘린더') || lowerTitle.includes('월별')) {
        return <Calendar size={size} />;
    }
    if (lowerTitle.includes('아이디어') || lowerTitle.includes('제안')) {
        return <Lightbulb size={size} />;
    }
    if (lowerTitle.includes('이미지') || lowerTitle.includes('사진') || lowerTitle.includes('갤러리')) {
        return <Image size={size} />;
    }

    // 외부 링크인 경우
    return <ExternalLink size={size} />;
};

// DB 아이콘 우선, 폴백으로 제목 기반 매핑
const getItemIcon = (item, size = 48) => {
    const LucideIcon = LUCIDE_ICON_MAP[item.icon];
    if (LucideIcon) return <LucideIcon size={size} />;
    return getIconByTitle(item.title, size);
};

// 제목 기반 설명 생성 함수
const getDescriptionByTitle = (title) => {
    const lowerTitle = title?.toLowerCase() || '';

    if (lowerTitle.includes('map group') || lowerTitle.includes('맵그룹')) {
        return '전사 영업 실적과 핵심 KPI를 통합 분석하는 대시보드';
    }
    if (lowerTitle.includes('직할본부')) {
        return '직할본부 월별·분기별 실적과 목표 달성률 분석';
    }
    if (lowerTitle.includes('1본부')) {
        return '1본부 영업 활동 현황과 주요 KPI 모니터링';
    }
    if (lowerTitle.includes('2본부')) {
        return '2본부 영업 활동 현황과 주요 KPI 모니터링';
    }
    if (lowerTitle.includes('3본부')) {
        return '3본부 영업 활동 현황과 주요 KPI 모니터링';
    }
    if (lowerTitle.includes('arena')) {
        return 'CRM과 영업 파이프라인을 통합 관리하는 핵심 시스템';
    }
    if (lowerTitle.includes('임차효율')) {
        return '임차 물건의 수익성과 지역별 효율성 분석';
    }
    if (lowerTitle.includes('대시보드') || lowerTitle.includes('dashboard')) {
        return '핵심 비즈니스 지표를 실시간 시각화하여 의사결정 지원';
    }
    if (lowerTitle.includes('시스템') || lowerTitle.includes('개발')) {
        return '업무 자동화와 효율성 향상을 위한 시스템 개발 현황';
    }
    if (lowerTitle.includes('교육') || lowerTitle.includes('학습')) {
        return '영업 역량 강화를 위한 직급별·직무별 맞춤 교육';
    }
    if (lowerTitle.includes('목표') || lowerTitle.includes('비전')) {
        return '조직의 비전과 전략적 방향성, 핵심 과제 공유';
    }
    if (lowerTitle.includes('성과') || lowerTitle.includes('평가')) {
        return '공정하고 투명한 성과 평가 체계와 보상 정책';
    }

    return '해당 자료의 상세 내용을 확인하실 수 있습니다.';
};

// 제목 기반 뱃지 생성 함수
const getBadgesByTitle = (title) => {
    const lowerTitle = title?.toLowerCase() || '';

    // 영업관리시트 - 대시보드들
    if (lowerTitle.includes('map group 대시보드') || lowerTitle.includes('맵그룹 대시보드')) {
        return [{ label: 'Dashboard', primary: true }, { label: '전사' }, { label: 'KPI' }];
    }
    if (lowerTitle.includes('직할본부 대시보드')) {
        return [{ label: 'Dashboard', primary: true }, { label: '직할본부' }, { label: '실적분석' }];
    }
    if (lowerTitle.includes('1본부 대시보드')) {
        return [{ label: 'Dashboard', primary: true }, { label: '1본부' }, { label: '실적분석' }];
    }
    if (lowerTitle.includes('2본부 대시보드')) {
        return [{ label: 'Dashboard', primary: true }, { label: '2본부' }, { label: '실적분석' }];
    }
    if (lowerTitle.includes('3본부 대시보드')) {
        return [{ label: 'Dashboard', primary: true }, { label: '3본부' }, { label: '실적분석' }];
    }

    // 영업시스템 개발 현황
    if (lowerTitle.includes('arena')) {
        return [{ label: 'CRM', primary: true }, { label: '영업관리' }, { label: '파이프라인' }];
    }
    if (lowerTitle.includes('임차효율')) {
        return [{ label: 'Analytics', primary: true }, { label: '수익분석' }, { label: '임차' }];
    }
    if (lowerTitle.includes('입사관리')) {
        return [{ label: 'System', primary: true }, { label: 'HR' }, { label: '입사관리' }];
    }
    if (lowerTitle.includes('리쿠르팅') || lowerTitle.includes('recruiting')) {
        return [{ label: 'Platform', primary: true }, { label: '채용' }, { label: '인재영입' }];
    }
    if (lowerTitle.includes('채널 바로가기') || lowerTitle.includes('map group 채널')) {
        return [{ label: 'Link', primary: true }, { label: '채널' }, { label: '바로가기' }];
    }

    // 카막지마
    if (lowerTitle.includes('카막지마 홈페이지')) {
        return [{ label: 'Website', primary: true }, { label: '카막지마' }, { label: '플랫폼' }];
    }
    if (lowerTitle.includes('카막지마 소개서')) {
        return [{ label: 'Document', primary: true }, { label: '카막지마' }, { label: '소개' }];
    }
    if (lowerTitle.includes('맵쳤다') || lowerTitle.includes('유튜브')) {
        return [{ label: 'Media', primary: true }, { label: '유튜브' }, { label: '콘텐츠' }];
    }

    // 직무설명자료
    if (lowerTitle.includes('nova project') || lowerTitle.includes('노바')) {
        return [{ label: 'Project', primary: true }, { label: '신입' }, { label: '온보딩' }];
    }
    if (lowerTitle.includes('신입 cis')) {
        return [{ label: 'Guide', primary: true }, { label: '신입' }, { label: '입문' }];
    }
    if (lowerTitle.includes('경력 cis')) {
        return [{ label: 'Guide', primary: true }, { label: '경력' }, { label: '전환' }];
    }
    if (lowerTitle.includes('브로슈어') || lowerTitle.includes('brochure')) {
        return [{ label: 'Brochure', primary: true }, { label: '소개' }, { label: '자료' }];
    }

    // 정례회의
    if (lowerTitle.includes('정례회의')) {
        return [{ label: 'Meeting', primary: true }, { label: '정기' }, { label: '회의록' }];
    }

    // DB자료
    if (lowerTitle.includes('db자료') || lowerTitle.includes('db 자료')) {
        return [{ label: 'Database', primary: true }, { label: '자료' }, { label: '데이터' }];
    }

    // 기본값
    return [{ label: 'Resource', primary: true }, { label: '자료' }, { label: '정보' }];
};

// 아이콘 매핑 함수 (이모지 기반 - 레거시)
const getIconComponent = (iconName, size = 48) => {
    const iconMap = {
        '📋': <FileText size={size} />,
        '📄': <FileText size={size} />,
        '🖼️': <Image size={size} />,
        '📅': <Calendar size={size} />,
        '🎯': <Target size={size} />,
        '📈': <TrendingUp size={size} />,
        '🏆': <Award size={size} />,
        '📚': <BookOpen size={size} />,
        '💼': <Briefcase size={size} />,
        '👥': <Users size={size} />,
        '📊': <BarChart3 size={size} />,
        '💡': <Lightbulb size={size} />,
        '🔗': <ExternalLink size={size} />,
    };
    return iconMap[iconName] || <FileText size={size} />;
};

function VisionPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { canAccessSection, getCategoriesBySection, getPartsByCategory, isLoading: permissionLoading } = usePermission();
    const { info, error: showError } = useAlert();

    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

    const [sections, setSections] = useState([]);
    const [dataLoading, setDataLoading] = useState(true);
    const [dataError, setDataError] = useState(null);

    // 활성 카테고리 (단일 선택 필터)
    const [activeCategory, setActiveCategory] = useState(null);

    useEffect(() => {
        if (!permissionLoading && !isAdmin && !canAccessSection('비전')) {
            navigate('/main');
        }
    }, [permissionLoading, isAdmin, canAccessSection, navigate]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentImages, setCurrentImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [modalTitle, setModalTitle] = useState('');
    const [isBrochure, setIsBrochure] = useState(false);
    const [hasMonthlyTabs, setHasMonthlyTabs] = useState(false);
    const [monthlyData, setMonthlyData] = useState({});
    const [selectedMonth, setSelectedMonth] = useState('');
    const [isFullscreen, setIsFullscreen] = useState(false);

    const [modalView, setModalView] = useState('closed');
    const [selectedPart, setSelectedPart] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [uploadGroups, setUploadGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);

    // PDF 지원을 위한 미디어 타입 상태
    const [mediaItems, setMediaItems] = useState([]);
    const [currentMediaType, setCurrentMediaType] = useState('image');

    // 썸네일 상태
    const [thumbnailUrls, setThumbnailUrls] = useState({});  // { partId: url }

    // 섹션 진입 시 모든 썸네일을 일괄 로딩하는 함수
    const loadAllThumbnails = useCallback(async (allItems) => {
        if (allItems.length === 0) return;

        const S3_URL_PATTERN = /^https:\/\/.*\.s3\..*\.amazonaws\.com\/(.+)$/;
        const batchItems = [];
        const immediateUrls = {};

        // 1단계: 직접 URL이 없는 항목은 UploadGroup 조회 (silent)
        const needsGroupLookup = allItems.filter(item => !item.thumbnailUrl);
        const groupResults = await Promise.allSettled(
            needsGroupLookup.map(async (item) => {
                const res = await UploadGroupsAPI.getByPartId(item.partId, { silent: true });
                const groups = res.data?.groups || res.groups || [];
                return { partId: item.partId, groups };
            })
        );

        // 2단계: 직접 URL이 있는 항목 분류
        for (const item of allItems) {
            if (item.thumbnailUrl) {
                const match = item.thumbnailUrl.match(S3_URL_PATTERN);
                if (match) {
                    batchItems.push({ id: String(item.partId), type: 'S3_KEY', value: decodeURIComponent(match[1]) });
                } else {
                    immediateUrls[item.partId] = item.thumbnailUrl;
                }
            }
        }

        // 3단계: UploadGroup 조회 결과 분류
        for (const result of groupResults) {
            if (result.status !== 'fulfilled') continue;
            const { partId, groups } = result.value;
            if (groups.length === 0) { immediateUrls[partId] = null; continue; }

            const group = groups[0];
            if (group.thumbnailS3Key) {
                batchItems.push({ id: String(partId), type: 'S3_KEY', value: group.thumbnailS3Key });
            } else if (group.thumbnailFileType === 'VIDEO') {
                immediateUrls[partId] = 'VIDEO_ICON';
            } else if (group.thumbnailUploadId) {
                batchItems.push({ id: String(partId), type: 'UPLOAD_ID', value: String(group.thumbnailUploadId), urlType: 'VIEW' });
            } else {
                immediateUrls[partId] = null;
            }
        }

        // 즉시 설정 가능한 URL 반영
        if (Object.keys(immediateUrls).length > 0) {
            setThumbnailUrls(prev => ({ ...prev, ...immediateUrls }));
        }

        // 4단계: 배치 API로 Presigned URL 일괄 발급
        if (batchItems.length > 0) {
            try {
                const batchRes = await S3API.batchPresign(batchItems);
                const results = batchRes.data?.results || batchRes.results || {};
                const urlMap = {};
                for (const [partId, data] of Object.entries(results)) {
                    urlMap[partId] = data ? data.url : null;
                }
                setThumbnailUrls(prev => ({ ...prev, ...urlMap }));
            } catch (error) {
                console.error('Failed to batch load thumbnails:', error);
                const failedMap = {};
                batchItems.forEach(item => { failedMap[item.id] = null; });
                setThumbnailUrls(prev => ({ ...prev, ...failedMap }));
            }
        }
    }, []);

    // 모달 상태 초기화 함수
    const resetModalState = () => {
        setIsModalOpen(false);
        setCurrentImages([]);
        setCurrentImageIndex(0);
        setModalTitle('');
        setIsBrochure(false);
        setHasMonthlyTabs(false);
        setMonthlyData({});
        setSelectedMonth('');
        setModalView('closed');
        setSelectedPart(null);
        setSelectedGroup(null);
        setUploadGroups([]);
        document.body.style.overflow = 'auto';
    };

    // 카테고리 선택 (단일 선택)
    const selectCategory = (category) => {
        // 모달이 열려있으면 닫기
        if (modalView !== 'closed') {
            resetModalState();
        }
        setActiveCategory(category);
    };

    // 전체보기
    const showAll = () => {
        // 모달이 열려있으면 닫기
        if (modalView !== 'closed') {
            resetModalState();
        }
        setActiveCategory(null);
    };

    const handleCardClick = async (card) => {
        console.log('=== Card Click Debug ===');
        console.log('Card:', card);
        console.log('hasGroups:', card.hasGroups);
        console.log('partId:', card.partId);
        console.log('partType:', card.partType);
        console.log('url:', card.url);

        // URL이 있으면 외부 링크로 이동
        if (card.url) {
            if (card.url.includes('example.com')) {
                info(`${card.title}\n\n실제 URL로 변경이 필요합니다.\n현재: ${card.url}`);
            } else {
                window.open(card.url, '_blank', 'noopener,noreferrer');
            }
            return;
        }

        // partId가 있으면 그룹 모달 시도 (hasGroups 여부와 관계없이)
        if (card.partId) {
            console.log('Opening groups modal for:', card.title);
            setSelectedPart(card);
            setModalTitle(card.title);
            setModalView('groups');
            document.body.style.overflow = 'hidden';
            await loadUploadGroups(card.partId);
            console.log('Modal view set to groups');
            return;
        }

        console.log('No partId - showing info message');
        info('이 항목에는 연결된 콘텐츠가 없습니다.');
    };

    const loadUploadGroups = async (partId) => {
        console.log('=== Loading Upload Groups ===');
        console.log('partId:', partId);
        setGroupsLoading(true);
        try {
            const response = await UploadGroupsAPI.getByPartId(partId);
            const groups = response.data?.groups || response.groups || [];
            console.log('Loaded groups:', groups);
            console.log('Groups count:', groups.length);
            setUploadGroups(groups);
        } catch (error) {
            console.error('Failed to load upload groups:', error);
            setUploadGroups([]);
        } finally {
            setGroupsLoading(false);
        }
    };

    const handleGroupSelect = async (group) => {
        setSelectedGroup(group);
        setGroupsLoading(true);
        try {
            const response = await UploadsAPI.getUploadsByGroupId(group.id, { limit: 1000 });
            const uploads = response.data?.uploads || response.uploads || [];

            // URL을 병렬로 요청
            const urlResults = await Promise.all(
                uploads.map(async (upload) => {
                    try {
                        const res = await S3API.getDownloadUrl(upload.id, 'VIEW');
                        return res.data?.url || res.url;
                    } catch (e) {
                        console.error('Failed to get presigned URL:', e);
                        return null;
                    }
                })
            );

            // 결과를 동기적으로 빌드 (순서 보장)
            const items = [];
            const urls = [];
            uploads.forEach((upload, index) => {
                const url = urlResults[index];
                if (url) {
                    const isPDF = upload.mimeType === 'application/pdf' ||
                                  upload.fileName?.toLowerCase().endsWith('.pdf');
                    const isVideo = upload.fileType === 'VIDEO' || upload.mimeType?.startsWith('video/');
                    items.push({
                        type: isPDF ? 'pdf' : isVideo ? 'video' : 'image',
                        src: url,
                        fileName: upload.originalName || upload.fileName
                    });
                    urls.push(url);
                }
            });

            setMediaItems(items);
            setCurrentImages(urls);
            setCurrentImageIndex(0);
            setCurrentMediaType(items[0]?.type || 'image');
            setModalView('files');
            setHasMonthlyTabs(false);
            setIsModalOpen(true);
        } catch (err) {
            console.error('Failed to load group files:', err);
            showError('파일을 불러오는데 실패했습니다.');
        } finally {
            setGroupsLoading(false);
        }
    };

    const handleBackToGroups = () => {
        setSelectedGroup(null);
        setCurrentImages([]);
        setMediaItems([]);
        setCurrentImageIndex(0);
        setCurrentMediaType('image');
        setModalView('groups');
        setIsModalOpen(false);
    };

    const closeModal = () => {
        resetModalState();
    };

    const nextImage = () => {
        const newIndex = (currentImageIndex + 1) % currentImages.length;
        setCurrentImageIndex(newIndex);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[newIndex]?.type || 'image');
        }
    };

    const prevImage = () => {
        const newIndex = (currentImageIndex - 1 + currentImages.length) % currentImages.length;
        setCurrentImageIndex(newIndex);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[newIndex]?.type || 'image');
        }
    };

    const handleMonthChange = (month) => {
        setSelectedMonth(month);
        setCurrentImages(monthlyData[month]);
        setCurrentImageIndex(0);
    };

    const downloadImage = (imageUrl, fileName) => {
        fetch(imageUrl)
            .then((response) => response.blob())
            .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName || 'image.png';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            })
            .catch((err) => {
                console.error('다운로드 실패:', err);
                showError('이미지 다운로드에 실패했습니다.');
            });
    };

    const downloadCurrentImage = () => {
        const imageUrl = currentImages[currentImageIndex];
        const fileName = `${modalTitle}_${currentImageIndex + 1}.png`;
        downloadImage(imageUrl, fileName);
    };

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    useEffect(() => {
        if (isModalOpen && currentImageIndex >= 0) {
            const thumbnailElement = document.querySelector(`.viewer-thumbnail:nth-child(${currentImageIndex + 1})`);
            if (thumbnailElement) {
                thumbnailElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center',
                });
            }
        }
    }, [currentImageIndex, isModalOpen]);

    useEffect(() => {
        if (currentImages.length > 1 && isModalOpen) {
            const nextIndex = (currentImageIndex + 1) % currentImages.length;
            const prevIndex = (currentImageIndex - 1 + currentImages.length) % currentImages.length;

            [nextIndex, prevIndex].forEach(idx => {
                if (idx !== currentImageIndex) {
                    const img = new window.Image();
                    img.src = currentImages[idx];
                }
            });
        }
    }, [currentImageIndex, currentImages, isModalOpen]);

    useEffect(() => {
        const loadVisionData = async () => {
            try {
                setDataLoading(true);
                setDataError(null);

                const menuResponse = await ContentsAPI.getMenu({ silent: true });
                const menuData = menuResponse.data || menuResponse;

                const visionSection = menuData.sections?.find(s => s.name === '비전');
                if (!visionSection) {
                    setSections([]);
                    return;
                }

                const transformedSections = (visionSection.categories || []).map((category) => {
                    const items = (category.parts || []).map((part) => {
                        const hasGroups = part.partType === 'GALLERY' || part.partType === 'MONTHLY_TABS';

                        return {
                            id: part.id,
                            partId: part.id,
                            title: part.name,
                            icon: part.icon || '📄',
                            buttonText: part.buttonText || '바로가기',
                            url: part.partType === 'LINK' ? part.externalUrl : null,
                            isBrochure: part.isBrochure || false,
                            hasGroups,
                            partType: part.partType,
                            categoryName: category.name,
                            thumbnailUrl: part.thumbnailUrl || null,
                        };
                    });

                    return {
                        id: category.id,
                        title: category.name,
                        icon: category.icon || '📋',
                        count: items.length,
                        items,
                    };
                });

                setSections(transformedSections);

                // 모든 썸네일 일괄 로딩 (silent - 로딩 스피너 미표시)
                const allParts = transformedSections.flatMap(section =>
                    section.items.map(item => ({
                        partId: item.partId,
                        thumbnailUrl: item.thumbnailUrl,
                    }))
                );
                loadAllThumbnails(allParts);
            } catch (error) {
                console.error('Failed to load vision data:', error);
                setDataError(error.message || '데이터를 불러오는데 실패했습니다.');
            } finally {
                setDataLoading(false);
            }
        };

        if (!permissionLoading && (isAdmin || canAccessSection('비전'))) {
            loadVisionData();
        }
    }, [permissionLoading, isAdmin, canAccessSection]);

    const filteredSections = useMemo(() => {
        if (isAdmin) {
            return sections;
        }

        const accessibleCategories = getCategoriesBySection('비전');
        const accessibleCategoryNames = accessibleCategories.map(c => c.name);

        return sections
            .filter(section => accessibleCategoryNames.includes(section.title))
            .map(section => {
                const accessibleParts = getPartsByCategory(section.title);
                const accessiblePartNames = accessibleParts.map(p => p.name);

                const filteredItems = section.items.filter(item =>
                    accessiblePartNames.includes(item.title)
                );

                return {
                    ...section,
                    items: filteredItems,
                    count: filteredItems.length
                };
            })
            .filter(section => section.items.length > 0);
    }, [isAdmin, sections, getCategoriesBySection, getPartsByCategory]);

    const allItems = useMemo(() => {
        const items = [];
        filteredSections.forEach(section => {
            section.items.forEach(item => {
                items.push({
                    ...item,
                    sectionTitle: section.title,
                    sectionIcon: section.icon,
                });
            });
        });
        return items;
    }, [filteredSections]);

    const categories = useMemo(() => {
        return filteredSections.map(s => s.title);
    }, [filteredSections]);

    // 필터링된 섹션 (단일 카테고리 선택)
    const displayedSections = useMemo(() => {
        if (activeCategory === null) {
            return filteredSections;
        }
        return filteredSections.filter(section =>
            section.title === activeCategory
        );
    }, [filteredSections, activeCategory]);

    const isLoading = permissionLoading || dataLoading;

    if (isLoading) {
        return (
            <div className="vision-page">
                <Navigation />
                <div className="vision-loading">
                    <div className="loading-spinner"></div>
                    <p>로딩 중...</p>
                </div>
            </div>
        );
    }

    if (dataError) {
        return (
            <div className="vision-page">
                <Navigation />
                <div className="vision-error">
                    <p className="error-title">오류가 발생했습니다</p>
                    <p className="error-message">{dataError}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="vision-page">
            <Navigation />

            <div className="vision-container">
                {/* 페이지 헤더 */}
                <header className="vision-header">
                    <h1 className="vision-title">비전 <span className="vision-title-en">Vision</span></h1>
                    <p className="vision-description">
                        목표와 미래를 함께 만들어가는 MAP Group의 비전 자료를 확인하세요
                    </p>
                </header>

                <div className="vision-layout">
                    {/* 메인 콘텐츠 영역 */}
                    <main className="vision-main">
                        {displayedSections.length === 0 ? (
                            <div className="vision-empty">
                                <p>접근 가능한 항목이 없습니다.</p>
                                <p className="empty-hint">관리자에게 권한을 요청해주세요.</p>
                            </div>
                        ) : (
                            <div className="category-list">
                                {displayedSections.map((section) => (
                                    <div key={section.id} className="category-section">
                                        {/* 카테고리 헤더 */}
                                        <div className="category-header">
                                            <h2 className="category-title">{section.title}</h2>
                                            <span className="category-count">{section.items.length}</span>
                                        </div>

                                        {/* 해당 카테고리의 아이템 리스트 */}
                                        <div className="category-items">
                                            {section.items.map((item) => (
                                                <article
                                                    key={item.id}
                                                    className="article-card"
                                                    onClick={() => handleCardClick(item)}
                                                >
                                                    <div className="article-content">
                                                        <div className="article-badges">
                                                            {getBadgesByTitle(item.title).map((badge, idx) => (
                                                                <span
                                                                    key={idx}
                                                                    className={`article-badge ${badge.primary ? '' : 'secondary'}`}
                                                                >
                                                                    {badge.label}
                                                                </span>
                                                            ))}
                                                        </div>
                                                        <h3 className="article-title">{item.title}</h3>
                                                        <p className="article-description">{getDescriptionByTitle(item.title)}</p>
                                                    </div>
                                                    <div className="article-thumbnail">
                                                        {thumbnailUrls[item.partId] === undefined ? (
                                                            <div className="thumbnail-skeleton"></div>
                                                        ) : thumbnailUrls[item.partId] === 'VIDEO_ICON' ? (
                                                            <div className="thumbnail-fallback video-thumbnail">
                                                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                                    <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" />
                                                                </svg>
                                                            </div>
                                                        ) : thumbnailUrls[item.partId] ? (
                                                            <>
                                                                <img
                                                                    src={thumbnailUrls[item.partId]}
                                                                    alt={item.title}
                                                                    className="thumbnail-image"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        if (e.target.nextSibling) {
                                                                            e.target.nextSibling.style.display = 'flex';
                                                                        }
                                                                    }}
                                                                />
                                                                <div className="thumbnail-fallback" style={{ display: 'none' }}>
                                                                    {getItemIcon(item, 36)}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="thumbnail-fallback">
                                                                {getItemIcon(item, 36)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </main>

                    {/* 사이드바 - 카테고리 필터 */}
                    {categories.length > 0 && (
                        <aside className="vision-sidebar">
                            <div className="sidebar-card">
                                <div className="sidebar-header">
                                    <span className="sidebar-title">카테고리</span>
                                </div>
                                <div className="sidebar-nav">
                                    <button
                                        className={`nav-item ${activeCategory === null ? 'active' : ''}`}
                                        onClick={showAll}
                                    >
                                        전체보기
                                    </button>
                                    {categories.map((category) => (
                                        <button
                                            key={category}
                                            className={`nav-item ${activeCategory === category ? 'active' : ''}`}
                                            onClick={() => selectCategory(category)}
                                        >
                                            {category}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </aside>
                    )}
                </div>
            </div>

            {/* 그룹 선택 모달 */}
            {modalView === 'groups' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="group-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="group-modal-header">
                            <div className="group-modal-title-section">
                                <h2 className="group-modal-title">{modalTitle}</h2>
                                <span className="group-modal-count">{uploadGroups.length}개 자료</span>
                            </div>
                            <button className="modal-close-btn" onClick={closeModal}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="group-modal-body">
                            {groupsLoading ? (
                                <div className="groups-loading">
                                    <div className="loading-spinner small"></div>
                                    <span>자료 목록을 불러오는 중...</span>
                                </div>
                            ) : uploadGroups.length === 0 ? (
                                <div className="groups-empty">
                                    <FolderOpen size={48} strokeWidth={1} />
                                    <span>등록된 자료가 없습니다.</span>
                                </div>
                            ) : (
                                <div className="groups-list">
                                    {uploadGroups.map((group) => (
                                        <div
                                            key={group.id}
                                            className="group-item"
                                            onClick={() => handleGroupSelect(group)}
                                        >
                                            <div className="group-item-icon">
                                                <FolderOpen size={24} />
                                            </div>
                                            <div className="group-item-info">
                                                <span className="group-item-title">{group.title}</span>
                                                <span className="group-item-count">{group.fileCount || 0}개 파일</span>
                                            </div>
                                            <ChevronRight size={20} className="group-item-arrow" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 이미지/PDF 뷰어 모달 */}
            {isModalOpen && modalView === 'files' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <button
                        className="viewer-nav-btn prev"
                        onClick={(e) => { e.stopPropagation(); prevImage(); }}
                        disabled={currentImages.length <= 1}
                    >
                        <ChevronLeft size={24} />
                    </button>

                    <div
                        className={`viewer-modal ${isFullscreen ? 'fullscreen' : ''} ${currentMediaType === 'pdf' && mediaItems.length <= 1 ? 'pdf-layout' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="viewer-header">
                            <div className="viewer-header-left">
                                <button className="viewer-back-btn" onClick={handleBackToGroups}>
                                    <ChevronLeft size={20} />
                                    <span>목록</span>
                                </button>
                                <h2 className="viewer-title">{selectedGroup?.title || modalTitle}</h2>
                                {hasMonthlyTabs && (
                                    <select
                                        className="month-dropdown"
                                        value={selectedMonth}
                                        onChange={(e) => handleMonthChange(e.target.value)}
                                    >
                                        {Object.keys(monthlyData).map((month) => (
                                            <option key={month} value={month}>{month}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="viewer-header-right">
                                <span className="viewer-counter">
                                    {currentImageIndex + 1} / {currentImages.length}
                                </span>
                                {currentMediaType !== 'pdf' && (
                                    <>
                                        <button className="viewer-action-btn" onClick={downloadCurrentImage} title="다운로드">
                                            <Download size={20} />
                                        </button>
                                        <button
                                            className="viewer-action-btn"
                                            onClick={() => setIsFullscreen(!isFullscreen)}
                                            title={isFullscreen ? '축소' : '전체화면'}
                                        >
                                            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                                        </button>
                                    </>
                                )}
                                <button
                                    className="viewer-close-btn"
                                    onClick={isFullscreen ? () => setIsFullscreen(false) : closeModal}
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="viewer-body">
                            {isFullscreen && (
                                <button
                                    className="fullscreen-close-btn"
                                    onClick={() => setIsFullscreen(false)}
                                    title="축소"
                                >
                                    <X size={28} />
                                </button>
                            )}

                            <div className="viewer-image-container">
                                {currentImages.length > 0 ? (
                                    currentMediaType === 'pdf' ? (
                                        <PDFViewer
                                            fileUrl={currentImages[currentImageIndex]}
                                            fileName={mediaItems[currentImageIndex]?.fileName}
                                        />
                                    ) : currentMediaType === 'video' ? (
                                        <video
                                            key={currentImages[currentImageIndex]}
                                            src={currentImages[currentImageIndex]}
                                            controls
                                            autoPlay
                                            className="viewer-video"
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                    ) : (
                                        <img
                                            src={currentImages[currentImageIndex]}
                                            alt={`${modalTitle} - ${currentImageIndex + 1}`}
                                            className={`viewer-image ${isBrochure ? 'brochure' : ''}`}
                                        />
                                    )
                                ) : (
                                    <div className="no-images">
                                        <Image size={48} strokeWidth={1} />
                                        <span>이미지가 없습니다</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="viewer-thumbnails">
                            {currentImages.map((src, index) => {
                                const mediaType = mediaItems[index]?.type;
                                return (
                                    <div
                                        key={index}
                                        className={`viewer-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                        onClick={() => {
                                            setCurrentImageIndex(index);
                                            if (mediaItems.length > 0) {
                                                setCurrentMediaType(mediaItems[index]?.type || 'image');
                                            }
                                        }}
                                    >
                                        {mediaType === 'pdf' ? (
                                            <FileText size={18} />
                                        ) : mediaType === 'video' ? (
                                            <Video size={18} />
                                        ) : (
                                            <Image size={18} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <button
                        className="viewer-nav-btn next"
                        onClick={(e) => { e.stopPropagation(); nextImage(); }}
                        disabled={currentImages.length <= 1}
                    >
                        <ChevronRight size={24} />
                    </button>
                </div>
            )}

            {/* 페이지 푸터 */}
            <footer className="vision-footer">
                <div className="footer-disclaimer">
                    <strong>본 CIS 자료는 MAP Group의 지식재산</strong>이며, 무단 복제·배포 시<br />
                    <span className="footer-highlight">비밀유지서약에 따른 법적 책임</span>을 부담해야 합니다.
                </div>
            </footer>
        </div>
    );
}

export default VisionPage;
